import instance from "../core/instance";
import { REGISTRATION_PLATFORM_MCARE } from "../constants/registrationPlatform";

export interface SignUpEmailPayload {
  email: string;
  password: string;
  fullname: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  referralCode?: string;
  couponCode?: string;
  /** Must be true when couponCode is sent; backend rejects if code sent without this. */
  couponApplied?: boolean;
  msisdn?: string;
  mcareId?: number;
  /** api-medpharma AuthCredentialDTO.registrationPlatform */
  registrationPlatform?: string;
}

export interface EmailSignupVerifyPayload {
  email: string;
  password: string;
  fullname: string;
  otpCode: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  couponCode?: string;
  /** Must be true when couponCode is sent; backend rejects if code sent without this. */
  couponApplied?: boolean;
  msisdn?: string;
  mcareId?: number;
  registrationPlatform?: "mcare" | "patient-website" | "doctor-website" | "patient-app" | "doctor-app";
  /** Referral share code, if signing up via a referral link. */
  shareCode?: string;
  deviceId?: string;
}

export interface SignUpPhonePayload {
  phoneNumber: string;
  fullname: string;
  recaptchaToken?: string;
  sessionInfo?: string;
  registrationPlatform?: string;
}

export interface SignInEmailPayload {
  email: string;
  password: string;
}

export interface SignInPhonePayload {
  phoneNumber: string;
}

export interface VerifyOTPPayload {
  email?: string;
  phoneNumber?: string;
  otpCode: string;
  fullname?: string;
  couponCode?: string;
  /** Must be true when couponCode is sent; backend rejects if code sent without this. */
  couponApplied?: boolean;
  mcareId?: number;
  /** api-medpharma PhoneAuthDTO.registrationPlatform */
  registrationPlatform?: string;
  /** Campaign tracking id (the campaign cookie id returned by the anonymous
   *  chat) — backend promotes the tracking row to signup/subscribed itself. */
  trackingId?: string;
}

export interface SetNewPasswordPayload {
  email: string;
  newPassword: string;
}

export interface UpdatePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface RefreshTokenPayload {
  refreshToken: string;
}

export interface GoogleTokenPayload {
  googleToken: string;
  platform: "web" | "android" | "ios";
  /** api-medpharma GoogleAuthDto.registrationPlatform */
  registrationPlatform?: string;
}

export interface AppleTokenPayload {
  appleToken: string;
  platform: "web" | "ios";
  /** api-medpharma AppleAuthDto.registrationPlatform */
  registrationPlatform?: string;
}

export interface MobileOAuthPayload {
  provider: "google" | "apple";
  token: string;
  platform: "android" | "ios";
  /** api-medpharma MobileOAuthDto.registrationPlatform */
  registrationPlatform?: string;
}

export interface LinkOAuthPayload {
  provider: "google" | "apple";
  token: string;
  platform: "web" | "android" | "ios";
}

export interface UnlinkOAuthPayload {
  provider: "google" | "apple";
}

export interface AuthResponse {
  user: {
    id: number;
    email?: string;
    phoneNumber?: string;
    fullname?: string;
    role?: string;
    passwordMigrated?: boolean;
    oauthProviders?: any;
  };
  token: string;
  refreshToken: string;
  subscriptionRequest?: {
    statusMessage: string;
    error?: boolean;
    packageType?: string;
    packageLabel?: string;
  };
}

export interface MigrationResponse {
  // Literal `true` (not `boolean`) - this shape only ever exists to signal
  // "migration required"; a false value wouldn't make sense here and would
  // otherwise prevent TypeScript from cleanly narrowing
  // `AuthResponse | MigrationResponse` via `"requiresMigration" in response`.
  requiresMigration: true;
  message: string;
  email?: string;
  phoneNumber?: string;
}

const AuthService = {
  // Email Sign Up
  signUpEmail: async (payload: SignUpEmailPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/signup", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Email Sign Up Verification (creates the user + issues tokens)
  signUpEmailVerify: async (
    payload: EmailSignupVerifyPayload,
  ): Promise<AuthResponse> => {
    const response = await instance.post("/auth/signup-email-verify", payload);
    return response.data;
  },

  // Phone Sign Up
  signUpPhone: async (payload: SignUpPhonePayload) => {
    const response = await instance.post("/auth/signup-phone", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Phone Sign Up Verification
  signUpVerify: async (payload: VerifyOTPPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/signup-verify", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Email/Password Sign In
  signInEmail: async (
    payload: SignInEmailPayload,
  ): Promise<AuthResponse | MigrationResponse> => {
    const response = await instance.post("/auth/signin", payload);
    return response.data;
  },

  // Phone Sign In (New Flow)
  signInPhoneNew: async (
    payload: SignInPhonePayload,
  ): Promise<{ message: string; phoneNumber: string } | MigrationResponse> => {
    const response = await instance.post("/auth/signin-phone", payload);
    return response.data;
  },

  // Phone Sign In (Legacy)
  signInPhone: async (payload: SignInPhonePayload) => {
    const response = await instance.post("/auth/signin-phone", payload);
    return response.data;
  },

  // Phone Sign In Verification (Legacy)
  signInVerify: async (payload: VerifyOTPPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/signin-verify", payload);
    return response.data;
  },

  // Verify OTP for Login
  verifyOTPLogin: async (payload: VerifyOTPPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/verify-otp-login", payload);
    return response.data;
  },

  // Verify OTP for Migration
  verifyOTPMigration: async (payload: VerifyOTPPayload) => {
    const response = await instance.post("/auth/verify-otp-migration", payload);
    return response.data;
  },

  // Set New Password (after migration)
  setNewPassword: async (
    payload: SetNewPasswordPayload,
  ): Promise<AuthResponse> => {
    const response = await instance.post("/auth/set-new-password", payload);
    return response.data;
  },

  // Update Password (authenticated user)
  updatePassword: async (payload: UpdatePasswordPayload) => {
    const response = await instance.post("/auth/me/update-password", payload);
    return response.data;
  },

  // Refresh Token
  refreshToken: async (payload: RefreshTokenPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/refresh-token", payload);
    return response.data;
  },

  // Sign Out
  signOut: async () => {
    const response = await instance.get("/auth/signout");
    return response.data;
  },

  // Google Token Authentication
  googleToken: async (payload: GoogleTokenPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/google-token", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Apple Token Authentication
  appleToken: async (payload: AppleTokenPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/apple-token", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Mobile OAuth
  mobileOAuth: async (payload: MobileOAuthPayload): Promise<AuthResponse> => {
    const response = await instance.post("/auth/mobile-oauth", {
      ...payload,
      registrationPlatform:
        payload.registrationPlatform ?? REGISTRATION_PLATFORM_MCARE,
    });
    return response.data;
  },

  // Get Linked OAuth Accounts
  getLinkedAccounts: async () => {
    const response = await instance.get("/auth/oauth/accounts");
    return response.data;
  },

  // Link OAuth Account
  linkOAuth: async (payload: LinkOAuthPayload) => {
    const response = await instance.post("/auth/oauth/link", payload);
    return response.data;
  },

  // Unlink OAuth Account
  unlinkOAuth: async (payload: UnlinkOAuthPayload) => {
    const response = await instance.post("/auth/oauth/unlink", payload);
    return response.data;
  },
};

export default AuthService;
